import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import PastVisits from "../doctor-console/PastVisits/PastVisits";
import { fetchPatientCareContexts, isAbortError } from "./api";
import { PR_COLORS } from "./constants";

/**
 * External record retrieval, behind a deliberate extra step (ADR-0003).
 *
 * Collapsed on arrival and on every Patient change: retrieval is slow, depends on
 * ABDM and remote facilities being reachable, and is consent-bearing — so it is a
 * decision the user makes, never a side effect of opening a Patient.
 *
 * Collapsed-by-default is this component's own explicit state. PastVisits also
 * makes its own collapse decision by inspecting the URL path; because this module
 * sits on that same unchanged route, that check behaves exactly as it did on the
 * previous screen.
 *
 * A failure inside this accordion must never degrade the Local Data above it.
 */
export default function HealthRecordsAccordion({ patient }) {
  const patientId = patient?.patient_id;

  const [expanded, setExpanded] = useState(false);
  const [careContextList, setCareContextList] = useState([]);
  const [fhirArray, setFhirArray] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Collapse and discard on Patient change, so one Patient's fetched records can
  // never be shown under another Patient's name.
  useEffect(() => {
    setExpanded(false);
    setCareContextList([]);
    setFhirArray(null);
    setError("");
  }, [patientId]);

  // Nothing is fetched until the user opens the accordion.
  useEffect(() => {
    if (!expanded || !patientId) return undefined;

    const controller = new AbortController();
    setLoading(true);
    setError("");

    fetchPatientCareContexts({ patientId, signal: controller.signal })
      .then((rows) => {
        if (controller.signal.aborted) return;
        // Handed over untouched, exactly as the previous screen did. PastVisits
        // reads raw API fields — notably `description`, which it pairs with
        // `care_context_reference` in its selector — so anything reshaped here
        // comes out as "undefined REF-xxxx" in that dropdown.
        setCareContextList(rows);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error("Failed to prepare health record retrieval:", err);
        setError(
          "Could not reach ABDM. Records from other facilities are unavailable right now.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [expanded, patientId]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(event, isExpanded) => setExpanded(isExpanded)}
      // PastVisits is heavy; unmounting it while shut keeps it from rendering and
      // running effects behind a collapsed panel.
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: "12px",
        border: `1px solid ${PR_COLORS.border}`,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        {/* Keeps the name this feature has always had. */}
        <Typography
          sx={{
            color: PR_COLORS.heading,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CloudDownloadOutlinedIcon /> Patient Historical Records
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        {error && <Alert severity="warning">{error}</Alert>}

        {loading && (
          <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography sx={{ color: PR_COLORS.muted, fontSize: "0.9rem" }}>
              Preparing health record retrieval…
            </Typography>
          </Box>
        )}

        {!loading && !error && (
          <PastVisits
            patientData={patient}
            careContextList={careContextList}
            fhirArrayData={fhirArray}
            onSubmitFhirArray={setFhirArray}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
